import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import { fmtMoney, fmtCount, plural } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const MONTHS_SHORT = ['', 'янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_FULL = ['', 'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
// N-089: money=true метрики графика — 2 знака после запятой (fmtMoney), остальные — целые (fmtCount)
const fmt = (n, money = false) => (money ? fmtMoney(n) : fmtCount(n));

// N-039: метаданные метрик, переключаемых на общем графике дашборда
const METRIC_META = {
  doctors_new: { label: 'Новых врачей', color: '#2EC4B6', money: false },
  patients_new: { label: 'Новых пациентов', color: '#6366f1', money: false },
  patients_reached: { label: 'Пациентов дошло', color: '#10b981', money: false },
  clinics_new: { label: 'Новых клиник', color: '#f59e0b', money: false },
  referrals: { label: 'Направлений', color: '#10b981', money: false },
  transactions: { label: 'Транзакций', color: '#3b82f6', money: false },
  commissions: { label: 'Комиссии врачей', color: '#2EC4B6', money: true },
  platform_fee: { label: 'Доход Refero', color: '#f59e0b', money: true },
  referred_all: { label: 'Оплачено клиникам всего', color: '#8b5cf6', money: true },
  referred_primary: { label: 'Оплачено — первичные визиты', color: '#06b6d4', money: true },
  referred_repeat: { label: 'Оплачено — повторные визиты', color: '#f472b6', money: true },
};
const GRANULARITY_LABEL = { day: 'День', week: 'Неделя', month: 'Месяц' };

function formatDMY(d) {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
function bucketDate(iso) {
  return new Date(iso + 'T00:00:00');
}
function bucketShortLabel(iso, granularity) {
  const d = bucketDate(iso);
  if (granularity === 'month') return MONTHS_SHORT[d.getMonth() + 1];
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function bucketFullLabel(iso, granularity) {
  const d = bucketDate(iso);
  if (granularity === 'month') return `${MONTHS_FULL[d.getMonth() + 1]} ${d.getFullYear()}`;
  if (granularity === 'week') {
    const end = new Date(d);
    end.setDate(end.getDate() + 6);
    return `${formatDMY(d)} – ${formatDMY(end)}`;
  }
  return formatDMY(d);
}

// YYYY-MM-DD для input[type=date]
function toInputDate(date) {
  return date.toISOString().split('T')[0];
}

function formatDateRu(str) {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  return `${d}.${m}.${y}`;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState('all'); // 'all' | 'range'
  const [dateFrom, setDateFrom] = useState(toInputDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo, setDateTo] = useState(toInputDate(new Date()));
  const [applied, setApplied] = useState(null); // { from, to } — применённый период
  const [activeMetric, setActiveMetric] = useState('platform_fee'); // N-039: метрика на общем графике
  const [granularity, setGranularity] = useState('month'); // 'day' | 'week' | 'month'
  const [series, setSeries] = useState([]);

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const load = () => {
    const params = applied ? { date_from: applied.from, date_to: applied.to } : {};
    axios.get(`${API}/admin/stats`, { headers, params }).then(r => setStats(r.data));
  };

  useEffect(() => { load(); }, [applied]);
  useEffect(() => {
    const params = { metric: activeMetric, granularity };
    if (applied) { params.date_from = applied.from; params.date_to = applied.to; }
    axios.get(`${API}/admin/stats/series`, { headers, params }).then(r => setSeries(r.data)).catch(() => setSeries([]));
    // eslint-disable-next-line
  }, [activeMetric, granularity, applied]);

  const applyPeriod = () => {
    if (!dateFrom || !dateTo) return;
    setApplied({ from: dateFrom, to: dateTo });
    setMode('range');
  };

  const resetPeriod = () => {
    setApplied(null);
    setMode('all');
  };

  if (!stats) return <Layout><div style={styles.loading}>Загрузка...</div></Layout>;

  const hasPeriod = mode === 'range' && applied;
  const pendingRequests = stats.pending_profile_requests || 0;
  const pendingTickets = stats.pending_tickets || 0;

  const periodLabel = hasPeriod
    ? `${formatDateRu(applied.from)} — ${formatDateRu(applied.to)}`
    : 'за всё время';

  const cards = [
    {
      label: 'Врачей',
      value: stats.doctors_count,
      delta: stats.doctors_delta,
      color: '#2EC4B6',
      href: '/doctors',
      metric: 'doctors_new',
      info: 'dash_doctors',
    },
    {
      label: 'Пациентов',
      value: stats.patients_count,
      delta: stats.patients_delta,
      color: '#6366f1',
      href: '/patients',
      metric: 'patients_new',
      info: 'dash_patients',
    },
    {
      label: 'Пациентов дошли',
      value: stats.patients_reached,
      delta: stats.patients_reached_delta,
      color: '#10b981',
      href: '/patients?paid=gt0',
      metric: 'patients_reached',
      info: 'dash_patients_reached',
    },
    {
      label: 'Клиник',
      value: stats.clinics_count,
      delta: stats.clinics_delta,
      color: '#f59e0b',
      href: '/clinics',
      metric: 'clinics_new',
      info: 'dash_clinics',
    },
    {
      label: 'Направлений',
      value: stats.referrals_count,
      color: '#10b981',
      isFlow: true,
      metric: 'referrals',
      info: 'dash_referrals',
    },
    {
      label: 'Транзакций',
      value: stats.transactions_count,
      color: '#3b82f6',
      metric: 'transactions',
      isFlow: true,
      href: '/transactions',
      info: 'dash_transactions',
    },
    {
      label: 'Комиссии врачей',
      value: fmtMoney(stats.total_commissions_paid) + ' ₽',
      color: '#2EC4B6',
      isFlow: true,
      href: '/commissions',
      size: 1.5,
      metric: 'commissions',
      info: 'dash_commissions',
    },
    {
      label: 'Доход Refero',
      value: fmtMoney(stats.total_platform_fee) + ' ₽',
      color: '#f59e0b',
      isFlow: true,
      size: 1.5,
      metric: 'platform_fee',
      info: 'dash_platform_fee',
    },
    {
      label: 'Оплачено клиникам всего',
      value: fmtMoney(stats.total_referred_all) + ' ₽',
      color: '#8b5cf6',
      isFlow: true,
      size: 1.5,
      metric: 'referred_all',
      info: 'dash_referred_all',
    },
    {
      label: 'Оплачено — первичные визиты',
      value: fmtMoney(stats.total_referred_primary) + ' ₽',
      color: '#06b6d4',
      isFlow: true,
      size: 1.5,
      metric: 'referred_primary',
      info: 'dash_referred_primary',
    },
    {
      label: 'Оплачено — повторные визиты',
      value: fmtMoney(stats.total_referred_repeat) + ' ₽',
      color: '#f472b6',
      isFlow: true,
      size: 1.5,
      metric: 'referred_repeat',
      info: 'dash_referred_repeat',
    },
  ];

  return (
    <Layout>
      <div style={styles.header}>
        <h1 style={styles.title}>Дашборд</h1>

        <div style={styles.filterBar}>
          {/* Кнопка Всё время */}
          <button
            style={{ ...styles.modeBtn, ...(mode === 'all' ? styles.modeBtnActive : {}) }}
            onClick={resetPeriod}
          >
            Всё время
          </button>

          {/* Date pickers */}
          <div style={styles.dateGroup}>
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={e => setDateFrom(e.target.value)}
              style={styles.dateInput}
            />
            <span style={styles.dateSep}>—</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={toInputDate(new Date())}
              onChange={e => setDateTo(e.target.value)}
              style={styles.dateInput}
            />
            <button style={styles.applyBtn} onClick={applyPeriod}>
              Применить
            </button>
          </div>
        </div>
      </div>

      {hasPeriod && (
        <div style={styles.hint}>
          Врачи, пациенты и клиники — текущее состояние, в скобках прирост за период.
          Направления, транзакции, комиссии, доход Refero и оплаты клиникам — точно за {periodLabel}.
        </div>
      )}

      {pendingRequests > 0 && (
        <a href="/profile-requests" style={styles.requestBanner}>
          📝 {pendingRequests} {plural(pendingRequests, ['заявка', 'заявки', 'заявок'])} на изменение профиля врача {plural(pendingRequests, ['ждёт', 'ждут', 'ждут'])} рассмотрения →
        </a>
      )}

      {pendingTickets > 0 && (
        <a href="/tickets" style={styles.requestBanner}>
          🎫 {pendingTickets} {plural(pendingTickets, ['тикет', 'тикета', 'тикетов'])} техподдержки {plural(pendingTickets, ['ждёт', 'ждут', 'ждут'])} ответа →
        </a>
      )}

      {(stats.clinic_api || stats.network_api) && (
        <div style={styles.apiCard}>
          <div style={styles.apiCardTitle}>Статус API<StatusInfo def="api" /></div>
          <div style={styles.apiRows}>
            <a href="/clinics" style={styles.apiRow}>
              <span style={styles.apiRowLabel}>Клиники →</span>
              <ApiChips counts={stats.clinic_api} />
            </a>
            <a href="/networks" style={styles.apiRow}>
              <span style={styles.apiRowLabel}>Сети →</span>
              <ApiChips counts={stats.network_api} />
            </a>
          </div>
        </div>
      )}

      <div style={styles.grid}>
        {cards.map(card => {
          const isActiveChart = card.metric === activeMetric;
          const inner = (
            <div style={{ ...styles.card, ...(card.href ? styles.cardClickable : {}) }}>
              <div style={styles.cardLabel}>
                <span style={styles.cardLabelText}>
                  {card.label}
                  {card.info && <StatusInfo def={card.info} />}
                </span>
                {card.href && <span style={styles.cardArrow}>→</span>}
              </div>
              <div style={{ ...styles.cardValue, color: card.color }}>{card.value}</div>
              {/* Дельта для срезовых */}
              {!card.isFlow && hasPeriod && card.delta != null && (
                <div style={styles.delta}>+{card.delta} за период</div>
              )}
              {/* Подпись для накопительных */}
              {card.isFlow && (
                <div style={styles.periodTag}>{periodLabel}</div>
              )}
              {card.metric && (
                <button
                  title={`Показать «${card.label}» на графике`}
                  style={{ ...styles.chartIconBtn, ...(isActiveChart ? styles.chartIconBtnActive : {}) }}
                  onClick={e => { e.preventDefault(); e.stopPropagation(); setActiveMetric(card.metric); }}
                >
                  <ChartIcon active={isActiveChart} />
                </button>
              )}
            </div>
          );

          const wideStyle = { gridColumn: `span ${Math.round((card.size || 1) * 2)}` };
          return card.href ? (
            <a key={card.label} href={card.href} style={{ ...styles.cardLink, ...wideStyle }}>
              {inner}
            </a>
          ) : (
            <div key={card.label} style={wideStyle}>{inner}</div>
          );
        })}
      </div>

      <div style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <div style={styles.chartTitle}>{METRIC_META[activeMetric].label} — по {(
            { day: 'дням', week: 'неделям', month: 'месяцам' }
          )[granularity]}</div>
          <div style={styles.granGroup}>
            {['day', 'week', 'month'].map(g => (
              <button
                key={g}
                style={{ ...styles.granBtn, ...(granularity === g ? styles.granBtnActive : {}) }}
                onClick={() => setGranularity(g)}
              >
                {GRANULARITY_LABEL[g]}
              </button>
            ))}
          </div>
        </div>
        <AreaChart series={series} color={METRIC_META[activeMetric].color} isMoney={METRIC_META[activeMetric].money} granularity={granularity} />
      </div>
    </Layout>
  );
}

// ── Лёгкий area-чарт на SVG, без зависимостей, с тултипом при наведении (как в кабинете врача) ──
// N-039: обобщён под произвольную метрику/гранулярность — series = [{bucket: 'YYYY-MM-DD', value}]
function AreaChart({ series, color = '#f59e0b', isMoney = false, granularity = 'month' }) {
  const [hover, setHover] = useState(null);
  const W = 760, H = 240, padL = 48, padR = 16, padT = 16, padB = 28;
  const max = Math.max(1, ...series.map(p => p.value));
  const niceMax = isMoney ? (Math.ceil(max / 2000) * 2000 || 2000) : (Math.ceil(max / 5) * 5 || 5);
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i) => padL + (series.length === 1 ? plotW / 2 : (i / (series.length - 1)) * plotW);
  const y = (v) => padT + plotH - (v / niceMax) * plotH;

  const pts = series.map((p, i) => `${x(i)},${y(p.value)}`);
  const linePath = 'M' + pts.join(' L');
  const areaPath = series.length ? `${linePath} L${x(series.length - 1)},${padT + plotH} L${x(0)},${padT + plotH} Z` : '';
  const ticks = [0, niceMax / 4, niceMax / 2, (niceMax * 3) / 4, niceMax];
  const hp = hover != null ? series[hover] : null;
  const labelStep = Math.max(1, Math.ceil(series.length / 12));
  const tickLabel = (t) => (t === 0 ? '0' : isMoney ? `${(t / 1000)}т` : `${Math.round(t)}`);
  const valueLabel = (v) => (isMoney ? `${fmt(v, true)} ₽` : `${fmt(v)}`);

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <linearGradient id="adminAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke="#334155" strokeWidth="1" />
            <text x={padL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#64748b">{tickLabel(t)}</text>
          </g>
        ))}
        <path d={areaPath} fill="url(#adminAreaFill)" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {hp && <line x1={x(hover)} y1={padT} x2={x(hover)} y2={padT + plotH} stroke={color} strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />}
        {series.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.value)} r={hover === i ? 5 : 3} fill={hover === i ? color : '#1e293b'} stroke={color} strokeWidth="2" />
        ))}
        {series.map((p, i) => (
          (i % labelStep === 0 || i === series.length - 1) && (
            <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill={hover === i ? color : '#64748b'} fontWeight={hover === i ? 700 : 400}>
              {bucketShortLabel(p.bucket, granularity)}
            </text>
          )
        ))}
        {series.map((p, i) => (
          <rect key={i} x={x(i) - plotW / series.length / 2} y={padT} width={plotW / series.length} height={plotH}
            fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }} />
        ))}
      </svg>
      {hp && (
        <div style={{
          position: 'absolute', left: `${(x(hover) / W) * 100}%`, top: `${(y(hp.value) / H) * 100}%`,
          transform: 'translate(-50%, -125%)', pointerEvents: 'none',
          background: '#0f172a', color: '#fff', borderRadius: 10, padding: '8px 12px', whiteSpace: 'nowrap',
          boxShadow: '0 6px 20px rgba(0,0,0,0.4)', border: '1px solid #334155', zIndex: 2,
        }}>
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>{bucketFullLabel(hp.bucket, granularity)}</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{valueLabel(hp.value)}</div>
        </div>
      )}
    </div>
  );
}

function ChartIcon({ active }) {
  const c = active ? '#2EC4B6' : '#64748b';
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M4 20V10M12 20V4M20 20V14" stroke={c} strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function ApiChips({ counts }) {
  const c = counts || { active: 0, failed: 0, untested: 0 };
  return (
    <span style={styles.apiChips}>
      <span style={{ ...styles.apiChip, color: '#4ade80' }}>🟢 {c.active}</span>
      <span style={{ ...styles.apiChip, color: '#f87171' }}>🔴 {c.failed}</span>
      <span style={{ ...styles.apiChip, color: '#94a3b8' }}>⚪ {c.untested}</span>
    </span>
  );
}

const styles = {
  loading: { color: '#64748b', fontSize: 16 },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 16, flexWrap: 'wrap', gap: 12,
  },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  modeBtn: {
    background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
    padding: '7px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500,
  },
  modeBtnActive: { background: '#2EC4B6', color: 'white', border: '1px solid #2EC4B6' },
  dateGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  dateInput: {
    background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155',
    borderRadius: 8, padding: '6px 10px', fontSize: 13, outline: 'none',
    colorScheme: 'dark',
  },
  dateSep: { color: '#64748b', fontSize: 13 },
  applyBtn: {
    background: '#1B2B4B', color: '#2EC4B6', border: '1px solid #2EC4B6',
    padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600,
  },
  hint: {
    fontSize: 12, color: '#64748b', background: '#1e293b',
    borderRadius: 8, padding: '10px 16px', marginBottom: 20,
    borderLeft: '3px solid #334155',
  },
  requestBanner: {
    display: 'block', fontSize: 13, color: '#fcd34d', background: '#422006',
    borderRadius: 8, padding: '12px 16px', marginBottom: 20,
    border: '1px solid #854d0e', textDecoration: 'none', fontWeight: 600,
  },
  apiCard: {
    background: '#1e293b', borderRadius: 12, padding: '16px 20px', marginBottom: 20,
  },
  apiCardTitle: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center' },
  apiRows: { display: 'flex', gap: 24, flexWrap: 'wrap' },
  apiRow: { display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' },
  apiRowLabel: { fontSize: 14, color: '#e2e8f0', fontWeight: 600 },
  apiChips: { display: 'flex', gap: 10 },
  apiChip: { fontSize: 13, fontWeight: 600 },
  // N-073: половинные колонки — позволяют размеру карточки быть 1 (span 2), 1.5 (span 3) или 2 (span 4)
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 16 },
  cardLink: { textDecoration: 'none', display: 'block' },
  card: { background: '#1e293b', borderRadius: 12, padding: 24, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 150, boxSizing: 'border-box', justifyContent: 'flex-start', position: 'relative' },
  cardClickable: { cursor: 'pointer' },
  cardLabel: { fontSize: 13, color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardLabelText: { display: 'inline-flex', alignItems: 'center' },
  cardArrow: { color: '#475569', fontSize: 14 },
  cardValue: { fontSize: 28, fontWeight: 700 },
  delta: { fontSize: 11, color: '#4ade80' },
  periodTag: { fontSize: 11, color: '#475569' },
  chartIconBtn: {
    position: 'absolute', right: 12, bottom: 12, width: 26, height: 26,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer',
  },
  chartIconBtnActive: { borderColor: '#2EC4B6', background: 'rgba(46,196,182,0.12)' },
  chartCard: { background: '#1e293b', borderRadius: 12, padding: 24, marginTop: 20 },
  chartHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  chartTitle: { fontSize: 15, fontWeight: 700, color: '#e2e8f0' },
  granGroup: { display: 'flex', gap: 6 },
  granBtn: {
    background: '#0f172a', color: '#94a3b8', border: '1px solid #334155',
    padding: '5px 12px', borderRadius: 7, fontSize: 12, cursor: 'pointer', fontWeight: 500,
  },
  granBtnActive: { background: '#2EC4B6', color: 'white', border: '1px solid #2EC4B6' },
};